import { useState, useEffect, useCallback, useRef } from 'react'
import {
  ArrowLeft, LayoutGrid, List, Search, Plus,
  Pencil, FileText, RefreshCw, Clock,
  CheckCircle, XCircle, Loader2, GitBranch,
  ChevronRight, Trash2, AlertTriangle,
  Home, Workflow, Plug, FileBarChart, BarChart2,
  Settings, HelpCircle, User, ChevronDown,
  Wrench, HardDrive, Activity, Sparkles, Database, Binary, TrendingUp,
  Building2, Briefcase, LogOut
} from 'lucide-react'
import ManageAccountsModal from '../landing/ManageAccountsModal'
import { getAPIBaseUrl } from '../../environment'
import NewRunModal from './NewRunModal'
import EditRunModal from './EditRunModal'
import ReRunModal from './ReRunModal'
import Modal, { FormActions, Button } from '../shared/Modal'
import AIAssistant from '../designer/AIAssistant'
import './RunsView.css'

const API_BASE = getAPIBaseUrl()

function formatRelativeTime(date) {
  if (!date) return '—'
  const now = new Date()
  const then = new Date(date)
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString()
}

function StatusBadge({ status }) {
  const norm = (status || '').toLowerCase()
  const map = {
    running:   { label: 'Running',   color: '#f59e0b', bg: 'rgba(245,158,11,0.15)',  Icon: () => <Loader2 size={11} className="spin" /> },
    completed: { label: 'Completed', color: '#3fb950', bg: 'rgba(63,185,80,0.15)',   Icon: () => <CheckCircle size={11} /> },
    failed:    { label: 'Failed',    color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   Icon: () => <XCircle size={11} /> },
  }
  const s = map[norm] || { label: status || 'Unknown', color: 'var(--text-muted)', bg: 'var(--bg-tertiary)', Icon: () => null }
  return (
    <span
      className="run-status-badge"
      style={{ color: s.color, backgroundColor: s.bg, borderColor: `${s.color}55` }}
    >
      <s.Icon />
      {s.label}
    </span>
  )
}

function RunsView({ project, onBack, onRunSelect, onNewRun, onGuidedAnalyze, onOpenReports, onOpenSettings, onOpenHelp, onGenerateDocs, onExtractBizLogic, onDecomposeCode, onGoToServiceGeneration, onGoToRefactorCode, onGoToRefineCode, onGoToJavaPkg, onJobMonitor, userProfile = {} }) {
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [showNewRunModal, setShowNewRunModal] = useState(false)
  const [editingRun, setEditingRun] = useState(null)
  const [deletingRun, setDeletingRun] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [reRunTarget, setReRunTarget] = useState(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showToolsMenu, setShowToolsMenu] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showManageAccounts, setShowManageAccounts] = useState(false)
  const toolsMenuRef = useRef(null)
  const userMenuRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target)) setShowToolsMenu(false)
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('modernizeit_token')
    localStorage.removeItem('modernizeit_user')
    window.location.reload()
  }

  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  const fetchRuns = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('modernizeit_token')
      const res = await fetch(`${API_BASE}/workflows/runs?account_id=${project.account}&app_name=${encodeURIComponent(project.name)}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setRuns(data.workflow_runs || [])
      }
    } catch (err) {
      console.error('Failed to fetch runs:', err)
    } finally {
      setLoading(false)
    }
  }, [project.account, project.name])

  useEffect(() => { fetchRuns() }, [fetchRuns])

  const filteredRuns = runs.filter(r => {
    const name = r.display_name || r.workflow_name || r.workflow_run_id || ''
    return name.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const getNodeCount = (run) => Object.keys(run.job_ids || {}).length

  const handleEditSaved = (updatedRun) => {
    setRuns(prev => prev.map(r =>
      r.workflow_run_id === updatedRun.workflow_run_id ? { ...r, ...updatedRun } : r
    ))
  }

  const handleDeleteConfirm = async () => {
    if (!deletingRun) return
    setDeleting(true)
    try {
      const token = localStorage.getItem('modernizeit_token')
      await fetch(`${API_BASE}/workflows/runs/${deletingRun.workflow_run_id}?account_id=${project.account}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setRuns(prev => prev.filter(r => r.workflow_run_id !== deletingRun.workflow_run_id))
    } catch (e) {
      console.error('Delete failed:', e)
    } finally {
      setDeleting(false)
      setDeletingRun(null)
    }
  }

  return (
    <div className="runs-view">

      {/* Top header — left: context | center: main nav | right: actions */}
      <div className="runs-header-bar">

        {/* Left: page context */}
        <div className="runs-header-left">
          <button className="runs-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Projects</span>
          </button>
          <div className="runs-breadcrumb">
            <span className="runs-bc-project">{project.name}</span>
            <ChevronRight size={14} className="runs-bc-sep" />
            <span className="runs-bc-page">Transformation Runs</span>
          </div>
        </div>

        {/* Center: main nav cluster */}
        <div className="runs-header-center">
          <div className="mode-switcher">
            <button className="mode-btn" onClick={onBack} title="Home">
              <Home size={16} />
              <span>Home</span>
            </button>
            <button
              className="mode-btn"
              onClick={() => {
                console.log('[RunsView] Analyze clicked for project:', project?.name, 'account:', project?.account)
                onGuidedAnalyze?.()
              }}
              title="Guided Analyze — review assets, execution map, and issues"
            >
              <BarChart2 size={16} />
              <span>Analyze</span>
            </button>
            <button className="mode-btn" onClick={() => onOpenReports()} title="Reports (opens in new tab)">
              <FileBarChart size={16} />
              <span>Reports</span>
            </button>

            {/* ── HITL More menu — hidden for now (not available from this view) ── */}
            {false && (
            <div className="more-menu-container">
              <button
                className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
                onClick={() => setShowMoreMenu(v => !v)}
                title="HITL Journey"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
                <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
              </button>

              {showMoreMenu && (
                <>
                  <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                  <div className="more-dropdown">
                    <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGuidedAnalyze?.() }}>
                      <span>Analyze</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGenerateDocs?.() }}>
                      <span>Generate Documentation</span>
                    </button>
                    {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onExtractBizLogic?.() }}>
                      <span>Extract Business Logic</span>
                    </button>
                    */}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onDecomposeCode?.() }}>
                      <span>Define Services</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}>
                      <span>Build Services</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}>
                      <span>Refactor Code</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                      <span>Refine Code</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}>
                      <span>Build / Package</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                      <span>Reports</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                      <span>Settings</span>
                    </button>
                  </div>
                </>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Right: Tools → Settings → Help → User */}
        <div className="runs-header-right">

          {/* Tools — hidden for now (customer questions, needs revisit) */}
          {false && (
          <div className="tools-menu-container" ref={toolsMenuRef}>
            <button className="icon-btn" onClick={() => setShowToolsMenu(!showToolsMenu)} title="Tools">
              <Wrench size={18} />
            </button>
            {showToolsMenu && (
              <div className="tools-dropdown">
                <div className="dropdown-section-label">UTILITIES</div>
                <button className="dropdown-item" disabled title="Coming soon"><HardDrive size={16} /><span>S3 Browser</span></button>
                <button className="dropdown-item" disabled><FileText size={16} /><span>System Logs</span></button>
                <div className="dropdown-divider" />
                <div className="dropdown-section-label">JOBS</div>
                <button className="dropdown-item" onClick={() => { setShowToolsMenu(false); onJobMonitor?.() }}><Activity size={16} /><span>Job Monitor</span></button>
                <div className="dropdown-divider" />
                <div className="dropdown-section-label">AI</div>
                <button className="dropdown-item" disabled><Sparkles size={16} /><span>Agent Studio</span></button>
                <div className="dropdown-divider" />
                <div className="dropdown-section-label">DATA</div>
                <button className="dropdown-item" disabled><Database size={16} /><span>Database Tools</span></button>
                <div className="dropdown-divider" />
                <div className="dropdown-section-label">CODE</div>
                <button className="dropdown-item" disabled title="Open from a workflow node gate check"><Binary size={16} /><span>Code Resolution Workbench</span></button>
                <div className="dropdown-divider" />
                <div className="dropdown-section-label">DASHBOARD</div>
                <button className="dropdown-item" disabled><TrendingUp size={16} /><span>Dashboard Editor</span></button>
              </div>
            )}
          </div>
          )}

          {/* Settings */}
          <button className="icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={18} />
          </button>

          {/* Help */}
          <button className="icon-btn" onClick={onOpenHelp} title="Help & Guidance">
            <HelpCircle size={18} />
          </button>

          {/* User */}
          <div className="user-menu-container" ref={userMenuRef}>
            <button className="user-menu-button" onClick={() => setShowUserMenu(!showUserMenu)} title={userProfile.email}>
              <User size={18} />
              <span className="user-name">{getShortName()}</span>
              <ChevronDown size={14} className={`chevron ${showUserMenu ? 'rotated' : ''}`} />
            </button>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <div className="user-dropdown-name">{userProfile.displayName || userProfile.firstName || 'User'}</div>
                  <div className="user-dropdown-email">{userProfile.email || ''}</div>
                </div>
                <div className="user-dropdown-divider"></div>
                <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); setShowManageAccounts(true) }}>
                  <Building2 size={16} />
                  <span>Manage Accounts</span>
                </button>
                <button className="user-dropdown-item" disabled title="Coming soon">
                  <Briefcase size={16} />
                  <span>Portfolio</span>
                </button>
                <div className="user-dropdown-divider"></div>
                <button className="user-dropdown-item logout" onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>

        </div>

      </div>

      {/* Main scrollable content */}
      <div className="runs-main">

        {/* Content header */}
        <div className="runs-content-header">
          <div className="runs-title-row">
            <h2>
              Transformation Runs
              {loading && <Loader2 size={15} className="spin" style={{ marginLeft: 8 }} />}
            </h2>
            {!loading && (
              <span className="runs-count-badge">{runs.length} Runs</span>
            )}
          </div>

          <div className="runs-controls">
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <LayoutGrid size={17} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List size={17} />
              </button>
            </div>

            <div className="search-box">
              <Search size={15} />
              <input
                type="text"
                placeholder="Search runs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button className="start-run-btn" onClick={() => setShowNewRunModal(true)}>
              <Plus size={15} />
              <span>Start New Run</span>
            </button>
          </div>
        </div>

        {/* Grid view */}
        {viewMode === 'grid' && (
          <div className="runs-grid">
            {filteredRuns.map((run) => {
              const displayName = run.display_name || run.workflow_name || `Run ${run.workflow_run_id.slice(-8)}`
              const nodeCount = getNodeCount(run)

              return (
                <div
                  key={run.workflow_run_id}
                  className="run-card"
                >
                  <div className="run-card-header">
                    <h3 className="run-card-title">{displayName}</h3>
                    <StatusBadge status={run.status} />
                  </div>

                  <div className="run-card-id">{run.workflow_run_id}</div>

                  <div className="run-card-details">
                    <div className="card-row">
                      <span className="card-label">Nodes:</span>
                      <span className="card-value">
                        {nodeCount > 0 ? `${nodeCount} completed` : '—'}
                      </span>
                    </div>
                    <div className="card-row">
                      <span className="card-label">Created:</span>
                      <span className="card-value">{formatRelativeTime(run.created_at)}</span>
                    </div>
                    {run.completed_at && (
                      <div className="card-row">
                        <span className="card-label">Completed:</span>
                        <span className="card-value">{formatRelativeTime(run.completed_at)}</span>
                      </div>
                    )}
                  </div>

                  <div className="run-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="run-action-btn"
                      title="Edit name & description"
                      onClick={(e) => { e.stopPropagation(); setEditingRun(run) }}
                    >
                      <Pencil size={12} />
                      <span>Edit</span>
                    </button>
                    <button
                      className="run-action-btn"
                      title="View reports"
                      onClick={(e) => { e.stopPropagation(); onOpenReports({ runId: run.workflow_run_id }) }}
                    >
                      <FileText size={12} />
                      <span>Reports</span>
                    </button>
                    <button
                      className="run-action-btn accent"
                      title="Temporarily disabled while source reuse is being reworked"
                      disabled
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RefreshCw size={12} />
                      <span>Re-run</span>
                    </button>
                    <button
                      className="run-action-btn danger"
                      title="Delete run"
                      onClick={(e) => { e.stopPropagation(); setDeletingRun(run) }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              )
            })}

            {filteredRuns.length === 0 && !loading && (
              <div className="no-runs">
                <GitBranch size={36} className="no-runs-icon" />
                {searchTerm ? (
                  <p>No runs matching &ldquo;{searchTerm}&rdquo;</p>
                ) : (
                  <>
                    <p>No transformation runs yet</p>
                    <p className="no-runs-hint">
                      Click &ldquo;Start New Run&rdquo; to kick off your first transformation
                    </p>
                    <button className="start-run-btn" onClick={() => setShowNewRunModal(true)}>
                      <Plus size={15} />
                      <span>Start New Run</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* List view */}
        {viewMode === 'list' && (
          <div className="runs-list">
            {filteredRuns.map((run) => {
              const displayName = run.display_name || run.workflow_name || `Run ${run.workflow_run_id.slice(-8)}`
              const nodeCount = getNodeCount(run)
              return (
                <button
                  key={run.workflow_run_id}
                  className="run-item"
                >
                  <GitBranch size={19} className="run-item-icon" />
                  <div className="run-item-info">
                    <span className="run-item-name">{displayName}</span>
                    <span className="run-item-meta">
                      <Clock size={11} />
                      <span>{formatRelativeTime(run.created_at)}</span>
                      {nodeCount > 0 && (
                        <>
                          <span className="run-meta-dot">·</span>
                          <span>{nodeCount} nodes</span>
                        </>
                      )}
                    </span>
                  </div>
                  <StatusBadge status={run.status} />
                  <ChevronRight size={16} className="run-item-arrow" />
                </button>
              )
            })}

            {filteredRuns.length === 0 && !loading && (
              <div className="no-runs" style={{ gridColumn: 'unset' }}>
                <p>No runs yet</p>
              </div>
            )}
          </div>
        )}

      </div>

      <NewRunModal
        isOpen={showNewRunModal}
        onClose={() => setShowNewRunModal(false)}
        project={project}
        onStartStandard={(runData) => {
          onNewRun({ ...runData, mode: 'standard' })
        }}
        onStartAdvanced={(runData) => {
          onNewRun(runData)
        }}
      />

      <EditRunModal
        isOpen={!!editingRun}
        onClose={() => setEditingRun(null)}
        run={editingRun}
        accountId={project.account}
        onSaved={handleEditSaved}
      />

      <ReRunModal
        isOpen={!!reRunTarget}
        onClose={() => setReRunTarget(null)}
        run={reRunTarget}
        project={project}
        onReviewInWorkflow={(run) => onRunSelect(run)}
        onStartReRun={(run, ingestInfo) => onNewRun({
          ...run,
          _isNew: true,
          mode: 'standard',
          preloadedIngest: ingestInfo,
        })}
      />

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deletingRun}
        onClose={() => setDeletingRun(null)}
        title="Delete Run"
        size="small"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertTriangle size={22} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Are you sure?
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                <strong>{deletingRun?.display_name || deletingRun?.workflow_name || 'This run'}</strong> will be removed from the runs list. The underlying job data is preserved for reporting.
              </p>
            </div>
          </div>
          <FormActions>
            <Button variant="secondary" onClick={() => setDeletingRun(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </FormActions>
        </div>
      </Modal>

      {/* Workspace Assistant */}
      <AIAssistant
        account={project?.account}
        application={project?.name}
        pageType="runs"
        pageContext={{
          project_name: project?.name || null,
          run_count: runs.length,
          has_runs: runs.length > 0,
        }}
      />

      <ManageAccountsModal
        isOpen={showManageAccounts}
        onClose={() => setShowManageAccounts(false)}
        onOpenSettings={onOpenSettings}
      />

    </div>
  )
}

export default RunsView
